import React from 'react'

import { useHistory } from 'react-router-dom'

import { ZariContext } from '_zari/context'
import { Portlet, Table, Button, Form, Col, Row } from '_zari/core'

import { CodigosTransaccionesConstant, EstadosRequerimiento, TiposTablaPersonalizadaConstant } from 'constants/index'

import { DownloadAxiosFile } from 'utils'

import * as RequerimientoService from 'services/requerimiento.service'
import * as UsuarioService from 'services/usuario.service'
import * as TablasPersonalizadasService from 'services/tablasPersonalizadas.service'

// Estados que un requerimiento puede tener al pasar por almacén (para el filtro)
const ESTADOS_FILTRO = [
  EstadosRequerimiento.EN_REVISION,
  EstadosRequerimiento.ELEVADO_A_GERENCIA,
  EstadosRequerimiento.APROBADO_POR_GERENCIA,
  EstadosRequerimiento.CON_ORDEN_DE_COMPRA,
  EstadosRequerimiento.POR_RECIBIR,
  EstadosRequerimiento.EN_ALMACEN,
  EstadosRequerimiento.ATENDIDO_PARCIALMENTE,
  EstadosRequerimiento.ATENDIDO_DESDE_ALMACEN,
  EstadosRequerimiento.CERRADO
].filter(Boolean)

const portletBreadcrumbs = [{ name: 'Almacén · Entradas y entregas' }]

const tableHeaderRows = [
  { label: 'CORR.', width: 70 },
  { label: 'OFICINA' },
  { label: 'SOLICITANTE' },
  { label: 'FEC. NEC.', width: 100 },
  { label: 'PRIORIDAD', width: 100 },
  { label: 'ÍTEMS PEND.', width: 110, align: 'center' },
  { label: 'P/ ENTREGA', width: 100, align: 'center' },
  { label: 'TIPO', width: 110, align: 'center' },
  { label: 'ESTADO', width: 130, align: 'center' },
  { label: '', width: 140, align: 'center' }
]

const ColorPrioridad = {
  ALTA: 'danger',
  NORMAL: 'secondary',
  BAJA: 'info'
}

const Lista = () => {
  const __zariContext = React.useContext(ZariContext)
  const history = useHistory()

  const [registros, setRegistros] = React.useState([])
  const [cargando, setCargando] = React.useState(false)

  const [filtroBusqueda, setFiltroBusqueda] = React.useState('')
  const [filtroFechaDesde, setFiltroFechaDesde] = React.useState('')
  const [filtroFechaHasta, setFiltroFechaHasta] = React.useState('')
  const [filtroOficina, setFiltroOficina] = React.useState('')
  const [filtroUsuario, setFiltroUsuario] = React.useState('')
  const [filtroEstado, setFiltroEstado] = React.useState('')
  const [oficinas, setOficinas] = React.useState([])
  const [usuarios, setUsuarios] = React.useState([])

  const [expandido, setExpandido] = React.useState({})

  const construirFiltros = React.useCallback(
    () => ({
      busqueda: filtroBusqueda || undefined,
      fechaDesde: filtroFechaDesde || undefined,
      fechaHasta: filtroFechaHasta || undefined,
      idOficina: filtroOficina || undefined,
      idUsuario: filtroUsuario || undefined,
      estado: filtroEstado || undefined
    }),
    [filtroBusqueda, filtroFechaDesde, filtroFechaHasta, filtroOficina, filtroUsuario, filtroEstado]
  )

  const cargar = React.useCallback(() => {
    setCargando(true)
    RequerimientoService.obtenerGestionEntregasAlmacen(construirFiltros())
      .then(({ data }) => setRegistros(Array.isArray(data) ? data : []))
      .catch(() => __zariContext.snack('error', 'No se pudo cargar la lista de entradas a almacén.'))
      .finally(() => setCargando(false))
  }, [__zariContext, construirFiltros])

  const descargarExcel = React.useCallback(() => {
    __zariContext.loader.open('Generando Excel...')
    RequerimientoService.descargarExcelEntregasAlmacen(construirFiltros())
      .then(({ data, headers }) => {
        DownloadAxiosFile.downloadAsBlob(data, headers, 'ReporteEntregasAlmacen.xlsx')
        __zariContext.snack('success', 'Reporte descargado.')
      })
      .catch(() => __zariContext.snack('error', 'No se pudo generar el reporte Excel.'))
      .finally(() => __zariContext.loader.close())
  }, [__zariContext, construirFiltros])

  const descargarPdf = React.useCallback(() => {
    __zariContext.loader.open('Generando PDF...')
    RequerimientoService.descargarPdfEntregasAlmacen(construirFiltros())
      .then(({ data, headers }) => {
        DownloadAxiosFile.downloadAsBlob(data, headers, 'ReporteEntregasAlmacen.pdf')
        __zariContext.snack('success', 'PDF generado.')
      })
      .catch(() => __zariContext.snack('error', 'No se pudo generar el PDF.'))
      .finally(() => __zariContext.loader.close())
  }, [__zariContext, construirFiltros])

  React.useEffect(() => {
    cargar()
    TablasPersonalizadasService.getAsSelect(TiposTablaPersonalizadaConstant.OFICINA)
      .then(({ data }) => setOficinas(data || []))
      .catch(() => {})
    UsuarioService.obtenerTodoAsSelect()
      .then(({ data }) => setUsuarios(data || []))
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const limpiar = () => {
    setFiltroBusqueda('')
    setFiltroFechaDesde('')
    setFiltroFechaHasta('')
    setFiltroOficina('')
    setFiltroUsuario('')
    setFiltroEstado('')
    setTimeout(cargar, 0)
  }

  const irADespachar = React.useCallback(
    idRequerimiento => {
      history.push(
        `/${CodigosTransaccionesConstant.INVENTARIOS_ENTRADASALIDA_NUEVO}?t=S&to=36&req=${idRequerimiento}`
      )
    },
    [history]
  )

  const toggleExpandir = id => {
    setExpandido(prev => ({ ...prev, [id]: !prev[id] }))
  }

  return (
    <Portlet>
      <Portlet.Header
        title='Almacén · Entradas y entregas'
        breadcrumbs={portletBreadcrumbs}
        buttons={[
          { name: <span><i className='fa fa-file-excel-o mr-1' />Excel</span>, onClick: descargarExcel },
          { name: <span><i className='fa fa-file-pdf-o mr-1' />PDF</span>, onClick: descargarPdf },
          { name: 'Refrescar', onClick: cargar }
        ]}
      />
      <Portlet.Body>
        <Row className='p-2 align-items-end'>
          <Col md={3}>
            <Form.Group>
              <Form.Label text='BÚSQUEDA' htmlFor='filtro-busqueda' md={4} />
              <Col md={8}>
                <Form.InputText
                  id='filtro-busqueda'
                  value={filtroBusqueda}
                  onChange={e => setFiltroBusqueda(e.target.value)}
                  placeholder='Correlativo o glosa'
                />
              </Col>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label text='DESDE' htmlFor='f-desde' md={4} />
              <Col md={8}>
                <Form.InputDate id='f-desde' value={filtroFechaDesde} onChange={e => setFiltroFechaDesde(e.target.value)} />
              </Col>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label text='HASTA' htmlFor='f-hasta' md={4} />
              <Col md={8}>
                <Form.InputDate id='f-hasta' value={filtroFechaHasta} onChange={e => setFiltroFechaHasta(e.target.value)} />
              </Col>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label text='OFICINA' htmlFor='f-ofic' md={4} />
              <Col md={8}>
                <Form.Select id='f-ofic' value={filtroOficina} onChange={e => setFiltroOficina(e.target.value)}>
                  <option value=''>Todas</option>
                  {oficinas.map(o => (
                    <option key={o._id} value={o._id}>{o.nombre}</option>
                  ))}
                </Form.Select>
              </Col>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label text='USUARIO' htmlFor='f-usu' md={4} />
              <Col md={8}>
                <Form.Select id='f-usu' value={filtroUsuario} onChange={e => setFiltroUsuario(e.target.value)}>
                  <option value=''>Todos</option>
                  {usuarios.map(u => (
                    <option key={u._id} value={u._id}>{u.username}</option>
                  ))}
                </Form.Select>
              </Col>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label text='ESTADO' htmlFor='f-est' md={4} />
              <Col md={8}>
                <Form.Select id='f-est' value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
                  <option value=''>Todos</option>
                  {ESTADOS_FILTRO.map(cod => (
                    <option key={cod} value={cod}>
                      {(EstadosRequerimiento.__dictionary[cod] && EstadosRequerimiento.__dictionary[cod].nombre) || cod}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Form.Group>
          </Col>
          <Col md={12} className='text-right pb-2'>
            <Button onClick={cargar} className='mr-1'>Buscar</Button>
            <Button outline color='secondary' onClick={limpiar}>Limpiar</Button>
          </Col>
        </Row>

        <div style={{ fontSize: '0.85rem', padding: '4px 12px', color: '#666' }}>
          {cargando ? 'Cargando...' : `${registros.length} requerimientos que pasaron por almacén`}
        </div>

        <Table
          headRows={tableHeaderRows}
          filas={registros}
          rowTemplate={(row, k) => {
            const colorPrioridad = ColorPrioridad[row.prioridad] || 'secondary'
            const estadoInfo = EstadosRequerimiento.__dictionary[row.estado] || null
            const filas = [
              <Table.Row key={`${row._id}-main`} hover>
                <Table.Cell><strong>{row.correlativo}</strong></Table.Cell>
                <Table.Cell>{row.oficina || '--'}</Table.Cell>
                <Table.Cell title={row.usuario}>{row.usuario || '--'}</Table.Cell>
                <Table.Cell>{row.fechaNecesidad || '--'}</Table.Cell>
                <Table.Cell>
                  <span className={`badge badge-${colorPrioridad}`}>{row.prioridad || 'NORMAL'}</span>
                </Table.Cell>
                <Table.Cell align='center'>
                  <strong style={{ color: row.itemsPendientes > 0 ? '#28a745' : '#999' }}>
                    {row.itemsPendientes}
                  </strong>
                  <span style={{ color: '#999' }}> / {row.totalItems}</span>
                </Table.Cell>
                <Table.Cell align='center'>
                  {row.itemsListosParaEntrega > 0 ? (
                    <span className='badge badge-success' title='Recibido en almacén, pendiente de entregar'>
                      {row.itemsListosParaEntrega} listo{row.itemsListosParaEntrega > 1 ? 's' : ''}
                    </span>
                  ) : (
                    <span style={{ color: '#bbb' }}>--</span>
                  )}
                </Table.Cell>
                <Table.Cell align='center'>
                  {row.flagCajaChica ? (
                    <span className='badge badge-warning'>CAJA CHICA</span>
                  ) : (
                    <span className='badge badge-light'>NORMAL</span>
                  )}
                </Table.Cell>
                <Table.Cell align='center'>
                  {estadoInfo ? (
                    <span className={`badge badge-${estadoInfo.color || 'secondary'}`}>{estadoInfo.nombre}</span>
                  ) : '--'}
                </Table.Cell>
                <Table.Cell align='center'>
                  <Button
                    size='sm'
                    outline
                    color='secondary'
                    onClick={() => toggleExpandir(row._id)}
                    style={{ marginRight: 4 }}
                    title={expandido[row._id] ? 'Ocultar items' : 'Ver items'}
                  >
                    <i className={`fa fa-chevron-${expandido[row._id] ? 'up' : 'down'}`} />
                  </Button>
                  <Button
                    size='sm'
                    color='success'
                    disabled={row.itemsPendientes === 0}
                    onClick={() => irADespachar(row._id)}
                    title='Despachar'
                  >
                    <i className='fa fa-paper-plane mr-1' />
                    Entregar
                  </Button>
                </Table.Cell>
              </Table.Row>
            ]

            if (expandido[row._id]) {
              filas.push(
                <Table.Row key={`${row._id}-detalle`}>
                  <Table.Cell colSpan={10} style={{ background: '#f8f9fa', padding: '8px 16px' }}>
                    <div style={{ fontSize: '0.85rem' }}>
                      {row.glosa && (
                        <div style={{ marginBottom: 6 }}>
                          <strong>Glosa:</strong> {row.glosa}
                        </div>
                      )}
                      <table className='table table-sm table-bordered mb-0' style={{ background: 'white' }}>
                        <thead>
                          <tr>
                            <th>CÓDIGO</th>
                            <th>PRODUCTO</th>
                            <th style={{ width: 80 }}>U. MED</th>
                            <th style={{ width: 80, textAlign: 'right' }}>SOLIC.</th>
                            <th style={{ width: 90, textAlign: 'right' }}>RECIBIDO</th>
                            <th style={{ width: 80, textAlign: 'right' }}>ATEND.</th>
                            <th style={{ width: 90, textAlign: 'right' }}>PEND.</th>
                            <th>OBS.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(row.items || []).length === 0 && (
                            <tr><td colSpan={8} className='text-center text-muted'>Sin items.</td></tr>
                          )}
                          {(row.items || []).map(it => (
                            <tr key={it._id}>
                              <td>{it.codigoProducto}</td>
                              <td>{it.nombreProducto}</td>
                              <td>{it.unidadMedida}</td>
                              <td className='text-right'>{it.cantidadTotal}</td>
                              <td className='text-right'>{it.cantidadRecibida}</td>
                              <td className='text-right'>{it.cantidadAtendidaAlmacen}</td>
                              <td className='text-right' style={{ color: it.cantidadPendiente > 0 ? '#28a745' : '#999', fontWeight: 'bold' }}>
                                {it.cantidadPendiente}
                              </td>
                              <td>{it.observacion || '--'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Table.Cell>
                </Table.Row>
              )
            }

            return filas
          }}
        />

        {registros.length === 0 && !cargando && (
          <div style={{ padding: 30, textAlign: 'center', color: '#999' }}>
            <i className='fa fa-inbox fa-2x' />
            <div style={{ marginTop: 8 }}>No hay requerimientos pendientes de entrega.</div>
          </div>
        )}
      </Portlet.Body>
    </Portlet>
  )
}

export default React.memo(Lista)
